import Vue from 'vue'
import { decode, parsePath, withoutBase, withoutTrailingSlash, normalizeURL } from 'ufo'

import { getMatchedComponentsInstances, getChildrenComponentInstancesUsingFetch, promisify, globalHandleError, urlJoin, sanitizeComponent } from './utils'
import NuxtError from '../app/layouts/error.vue'

import '../node_modules/@backmarket/design-system-vue2/dist/styles.css'

import '../app/app.css'

const _02169f65 = () => import('../app/layouts/default.translations.js'  /* webpackChunkName: "layouts/default.translations" */).then(m => sanitizeComponent(m.default || m))
const _6f6c098b = () => import('../app/layouts/default.vue'  /* webpackChunkName: "layouts/default" */).then(m => sanitizeComponent(m.default || m))
const _77430317 = () => import('../app/layouts/empty.vue'  /* webpackChunkName: "layouts/empty" */).then(m => sanitizeComponent(m.default || m))
const _e6fbf704 = () => import('../app/layouts/error.translations.js'  /* webpackChunkName: "layouts/error.translations" */).then(m => sanitizeComponent(m.default || m))
const _52cbc0eb = () => import('../app/layouts/minimal.vue'  /* webpackChunkName: "layouts/minimal" */).then(m => sanitizeComponent(m.default || m))
const _0dfac2de = () => import('../app/layouts/sav/customer/index.js'  /* webpackChunkName: "layouts/sav/customer/index" */).then(m => sanitizeComponent(m.default || m))
const _0af3bcd2 = () => import('../app/layouts/sav/customer/SAVCustomer.vue'  /* webpackChunkName: "layouts/sav/customer/SAVCustomer" */).then(m => sanitizeComponent(m.default || m))
const _690927b3 = () => import('../app/layouts/sav/platform/index.js'  /* webpackChunkName: "layouts/sav/platform/index" */).then(m => sanitizeComponent(m.default || m))
const _41d2bbd8 = () => import('../app/layouts/sav/platform/SAVPlatform.translations.js'  /* webpackChunkName: "layouts/sav/platform/SAVPlatform.translations" */).then(m => sanitizeComponent(m.default || m))
const _bc5b5308 = () => import('../app/layouts/sav/platform/SAVPlatform.vue'  /* webpackChunkName: "layouts/sav/platform/SAVPlatform" */).then(m => sanitizeComponent(m.default || m))
const _52712d3f = () => import('../app/layouts/withoutHeader.vue'  /* webpackChunkName: "layouts/withoutHeader" */).then(m => sanitizeComponent(m.default || m))

let resolvedLayouts = {}
const layouts = { "_default.translations": _02169f65,"_default": _6f6c098b,"_empty": _77430317,"_error.translations": _e6fbf704,"_minimal": _52cbc0eb,"_sav/customer/index": _0dfac2de,"_sav/customer/SAVCustomer": _0af3bcd2,"_sav/platform/index": _690927b3,"_sav/platform/SAVPlatform.translations": _41d2bbd8,"_sav/platform/SAVPlatform": _bc5b5308,"_withoutHeader": _52712d3f }

export default {
  render (h, props) {
    const layoutEl = h(this.layout || 'nuxt')
    const templateEl = h('div', {
      domProps: {
        id: '__layout'
      },
      key: this.layoutName
    }, [layoutEl])

    const transitionEl = h('transition', {
      props: {
        name: 'layout',
        mode: 'out-in'
      },
      on: {
        beforeEnter (el) {
          // Ensure to trigger scroll event after calling scrollBehavior
          window.$nuxt.$nextTick(() => {
            window.$nuxt.$emit('triggerScroll')
          })
        }
      }
    }, [templateEl])

    return h('div', {
      domProps: {
        id: '__nuxt'
      }
    }, [

      transitionEl
    ])
  },

  data: () => ({
    isOnline: true,

    layout: null,
    layoutName: '',

    nbFetching: 0
    }),

  beforeCreate () {
    Vue.util.defineReactive(this, 'nuxt', this.$options.nuxt)
  },
  created () {
    // Add this.$nuxt in child instances
    this.$root.$options.$nuxt = this

    if (process.client) {
      // add to window so we can listen when ready
      window.$nuxt = this

      this.refreshOnlineStatus()
      // Setup the listeners
      window.addEventListener('online', this.refreshOnlineStatus)
      window.addEventListener('offline', this.refreshOnlineStatus)
    }
    // Add $nuxt.error()
    this.error = this.nuxt.error
    // Add $nuxt.context
    this.context = this.$options.context
  },

  watch: {
    'nuxt.err': 'errorChanged'
  },

  computed: {
    isOffline () {
      return !this.isOnline
    },

    isFetching () {
      return this.nbFetching > 0
    },
  },

  methods: {
    refreshOnlineStatus () {
      if (process.client) {
        if (typeof window.navigator.onLine === 'undefined') {
          // If the browser doesn't support connection status reports
          // assume that we are online because most apps' only react
          // when they now that the connection has been interrupted
          this.isOnline = true
        } else {
          this.isOnline = window.navigator.onLine
        }
      }
    },

    async refresh () {
      const pages = getMatchedComponentsInstances(this.$route)

      if (!pages.length) {
        return
      }

      const promises = pages.map(async (page) => {
        let p = []

        // Old fetch
        if (page.$options.fetch && page.$options.fetch.length) {
          p.push(promisify(page.$options.fetch, this.context))
        }

        if (page.$options.asyncData) {
          p.push(
            promisify(page.$options.asyncData, this.context)
              .then((newData) => {
                for (const key in newData) {
                  Vue.set(page.$data, key, newData[key])
                }
              })
          )
        }

        // Wait for asyncData & old fetch to finish
        await Promise.all(p)
        // Cleanup refs
        p = []

        if (page.$fetch) {
          p.push(page.$fetch())
        }
        // Get all component instance to call $fetch
        for (const component of getChildrenComponentInstancesUsingFetch(page.$vnode.componentInstance)) {
          p.push(component.$fetch())
        }

        return Promise.all(p)
      })
      try {
        await Promise.all(promises)
      } catch (error) {
        globalHandleError(error)
        this.error(error)
      }
    },
    async errorChanged () {
      if (this.nuxt.err) {
        let errorLayout = (NuxtError.options || NuxtError).layout;

        if (typeof errorLayout === 'function') {
          errorLayout = errorLayout(this.context)
        }

        await this.loadLayout(errorLayout)

        this.setLayout(errorLayout)
      }
    },

    setLayout (layout) {
      if (!layout || !resolvedLayouts['_' + layout]) {
        layout = 'default'
      }
      this.layoutName = layout
      let _layout = '_' + layout
      this.layout = resolvedLayouts[_layout]
      return this.layout
    },
    loadLayout (layout) {
      const undef = !layout
      const nonexistent = !(layouts['_' + layout] || resolvedLayouts['_' + layout])
      let _layout = '_' + ((undef || nonexistent) ? 'default' : layout)
      if (resolvedLayouts[_layout]) {
        return Promise.resolve(resolvedLayouts[_layout])
      }
      return layouts[_layout]()
        .then((Component) => {
          resolvedLayouts[_layout] = Component
          delete layouts[_layout]
          return resolvedLayouts[_layout]
        })
        .catch((e) => {
          if (this.$nuxt) {
            return this.$nuxt.error({ statusCode: 500, message: e.message })
          }
        })
    },
  },
}
